import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import MobileNav from "@/components/MobileNav";
import { ArrowLeft, Upload, Image, Trash2, Plus, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const InspirationsPage = () => {
  const [inspirations, setInspirations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [notes, setNotes] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchInspirations();
  }, []);

  const fetchInspirations = async () => {
    try {
      const response = await axios.get(`${API}/inspirations`, { withCredentials: true });
      setInspirations(response.data);
    } catch (error) {
      console.error("Error fetching inspirations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setShowUploadModal(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);
    if (notes.trim()) {
      formData.append("notes", notes.trim());
    }

    try {
      const response = await axios.post(`${API}/inspirations/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      setInspirations((prev) => [response.data, ...prev]);
      toast.success("Inspiration added!");
      closeModal();
    } catch (error) {
      toast.error("Could not upload image");
    } finally {
      setUploading(false);
    }
  };

  const deleteInspiration = async (inspirationId) => {
    try {
      await axios.delete(`${API}/inspirations/${inspirationId}`, { withCredentials: true });
      setInspirations((prev) => prev.filter((i) => i.inspiration_id !== inspirationId));
      toast.success("Removed");
    } catch (error) {
      toast.error("Could not delete");
    }
  };

  const closeModal = () => {
    setShowUploadModal(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    setNotes("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getImageUrl = (inspiration) => {
    if (inspiration.image_url.startsWith("/api")) {
      return `${process.env.REACT_APP_BACKEND_URL}${inspiration.image_url}`;
    }
    return inspiration.image_url;
  };

  return (
    <div className="min-h-screen bg-white pb-32" data-testid="inspirations-page">
      {/* Header */}
      <header className="border-b border-gray-100 sticky top-0 z-40 bg-white/90 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/board" className="text-gray-500 hover:text-black">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-display text-xl font-bold">Inspirations</h1>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => window.open("https://pinterest.com", "_blank")}
              className="rounded-full border-gray-200"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Pinterest
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="bg-black hover:bg-gray-900 text-white rounded-full"
              data-testid="upload-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Upload
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Pinterest Banner */}
        <Card className="mb-6 border border-gray-200 bg-gray-50">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#E60023] flex items-center justify-center text-white font-bold">
              P
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-black">Connect Pinterest</h3>
              <p className="text-sm text-gray-600">Import your nail art boards directly</p>
            </div>
            <Badge variant="outline" className="text-gray-600">Coming Soon</Badge>
          </CardContent>
        </Card>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="aspect-square bg-gray-100 animate-pulse" />
              </div>
            ))}
          </div>
        ) : inspirations.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Image className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="font-display text-xl font-semibold text-black mb-2">No inspirations yet</h3>
            <p className="text-gray-600 mb-6 max-w-sm mx-auto">
              Upload photos of nail designs you love to share with your technician
            </p>
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="bg-black hover:bg-gray-900 text-white rounded-full"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Your First
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 stagger-children">
            {inspirations.map((insp) => (
              <Card
                key={insp.inspiration_id}
                className="border border-gray-200 overflow-hidden group"
                data-testid={`inspiration-${insp.inspiration_id}`}
              >
                <CardContent className="p-0 relative">
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={getImageUrl(insp)}
                      alt="Nail inspiration"
                      className="w-full h-full object-cover portfolio-image"
                    />
                  </div>
                  {insp.source === "pinterest" && (
                    <Badge className="absolute top-2 left-2 bg-[#E60023] text-white">
                      Pinterest
                    </Badge>
                  )}
                  {insp.notes && (
                    <div className="p-3 bg-white">
                      <p className="text-sm text-gray-700 line-clamp-2">{insp.notes}</p>
                    </div>
                  )}
                  <button
                    onClick={() => deleteInspiration(insp.inspiration_id)}
                    className="absolute top-2 right-2 p-2 bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                    data-testid={`delete-${insp.inspiration_id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white animate-fade-in">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-xl font-bold">Add Inspiration</h2>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {previewUrl && (
                <div className="relative aspect-square rounded-xl overflow-hidden mb-4 bg-gray-100">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <div className="mb-6">
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Notes (optional)
                </label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="What do you like about this design?"
                  className="resize-none rounded-xl border-gray-200 focus:ring-black focus:border-black"
                  rows={3}
                  data-testid="inspiration-notes"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={closeModal}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex-1 bg-black hover:bg-gray-900 text-white rounded-full"
                  data-testid="confirm-upload-btn"
                >
                  {uploading ? "Uploading..." : "Upload"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <MobileNav />
    </div>
  );
};

export default InspirationsPage;
